import * as preact from "preact";
import * as _ from "underscore";

import * as explanationReportModal from "explanationReportModal";
import * as practiceUtils from "practiceUtils";
import * as premiumUpsellModal from "premiumUpsellModal";
import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import * as subscriptionPlans from "shared/subscriptionPlans";
import { letter as L } from "shared/unicodePatterns";
import * as smartDictionaryUtils from "smartDictionaryUtils";
import stores from "stores/stores";
import TranslatableTextView, { TextSegment } from "views/TranslatableTextView";
import * as walkthrough from "walkthrough";

interface Props {
  word: string;
  context: string;
  language: string;
  showInFirstLanguage: boolean;
  cacheOnly?: boolean;
  showExplanationButton?: boolean;
  existingTranslations: string[];
  onSelectTranslation?: (translation: string) => void;
  onExplanationFetched?: () => void;
  onClickGenerate?: () => void;
  /**
   * Fired when the view settles with nothing to render (a dismissed word, or a
   * not-found result with no "Explain" button). Hosts that wrap the view in
   * their own chrome (e.g. the Web Reader pane) use this to close rather than
   * leave an empty box open.
   */
  onNoExplanation?: () => void;
}

interface State {
  status:
    | "loading"
    | "generated"
    | "error"
    | "not-found"
    | "generating"
    | "dismissed";
  explanation?: string;
  reachedLimit?: boolean;
  fetchTimeoutTimer: number | null;
}

const createRequestData = (props: Props) => ({
  query: props.word,
  context: props.context,
  language: props.language,
  explanationLanguage: props.showInFirstLanguage
    ? stores.user.firstLanguage.value
    : props.language,
});

export default class ExplanationView extends preact.Component<Props, State> {
  // Monotonically increasing id identifying the latest fetch invocation. Every
  // async completion path captures the id it was started with and bails unless
  // it is still the latest. Comparing request *data* is not enough: an
  // A → B → A click sequence (or retrying A while A's first fetch is still in
  // flight) produces repeated data, so a superseded completion would otherwise
  // look "valid" and clobber the current word's explanation or hijack its timer.
  private latestRequestId = 0;

  /** Cancels the in-flight streaming fetch when a newer request supersedes it */
  private abortController?: AbortController;

  constructor(props: Props) {
    super(props);

    this.state = {
      status: "loading",
      fetchTimeoutTimer: null,
    };

    setTimeout(() => {
      this.fetchExplanation();
    });
  }

  isCurrentRequest(requestId: number) {
    return this.latestRequestId === requestId;
  }

  explanationLanguage() {
    return this.props.showInFirstLanguage
      ? stores.user.firstLanguage.value
      : this.props.language;
  }

  // Records that the user has seen an AI explanation (the "Explain" feature)
  // and notifies the parent. The `explain` achievement gates the Explain
  // lifecycle email (see src/cron/sendLifecycleEmails.js). We don't set it on
  // the cacheOnly path (e.g. EditCardView passively showing a cached
  // explanation), so the achievement only reflects a genuine in-reader view.
  explanationShown() {
    if (!this.props.cacheOnly) {
      walkthrough.setAchievement("explain");
    }
    this.props.onExplanationFetched?.();
  }

  startFetchTimeoutTimer(requestId: number) {
    /** Fetching timeout threshold (ms) */
    const timeoutPeriod = 10 * 1000;

    this.stopFetchTimeoutTimer();
    this.setState({
      fetchTimeoutTimer: setTimeout(() => {
        // A superseded request (the user clicked another word) must not flip the
        // current explanation to an error when its stale timer finally fires.
        if (!this.isCurrentRequest(requestId)) {
          return;
        }
        this.setState({ status: "error" });
      }, timeoutPeriod),
    });
  }

  stopFetchTimeoutTimer() {
    if (this.state.fetchTimeoutTimer) {
      clearTimeout(this.state.fetchTimeoutTimer);
      this.setState({ fetchTimeoutTimer: null });
    }
  }

  isRequestDataStillValid(requestData) {
    return (
      JSON.stringify(requestData) ===
      JSON.stringify(createRequestData(this.props))
    );
  }

  async fetchExplanation() {
    const requestId = ++this.latestRequestId;

    // A superseded request still streaming is now unwanted: cancel it so the
    // server stops generating (and paying for) an explanation nobody will see.
    this.abortController?.abort();

    if (!this.props.cacheOnly) {
      await this.setState({ status: "generating" });
    }

    const requestData = createRequestData(this.props);
    const cachedExplanation = smartDictionaryUtils.cacheGet(requestData);

    if (cachedExplanation === null) {
      // The user dismissed explanations for this word + context
      this.setState({ status: "dismissed" });
      return;
    }

    if (cachedExplanation) {
      await this.setState({
        status: "generated",
        explanation: cachedExplanation,
      });

      // A newer fetch started while we awaited (props changed); let it win —
      // componentDidUpdate has already kicked off the refetch for the new word.
      if (!this.isCurrentRequest(requestId)) {
        return;
      }

      this.explanationShown();
      return;
    }

    if (
      this.state.status === "generated" ||
      this.state.status === "error" ||
      this.state.status === "not-found"
    ) {
      // Only auto-refresh if the cacheOnly prop is not set, otherwise it'll refetch on every
      // keystroke within EditCardView
      this.setState({
        status: "not-found",
      });
      return;
    }

    this.setState({ status: "loading" });

    if (!this.props.cacheOnly && readlangClient.canStreamResponses()) {
      this.setState({ status: "generating", explanation: "" });

      this.abortController = new AbortController();

      this.startFetchTimeoutTimer(requestId);
      fetch(
        `/api/smartDictionary?query=${encodeURIComponent(
          requestData.query
        )}&context=${encodeURIComponent(
          requestData.context
        )}&language=${encodeURIComponent(
          requestData.language
        )}&explanationLanguage=${requestData.explanationLanguage}`,
        { signal: this.abortController.signal }
      )
        .then((response) => {
          // The user may have clicked another word while this request was in
          // flight; if so, its result (dismissal, error, or stream) must not
          // overwrite the current word's explanation.
          if (!this.isCurrentRequest(requestId)) {
            return;
          }

          if (response?.status === 204) {
            // The user dismissed explanations for this word + context
            this.stopFetchTimeoutTimer();
            smartDictionaryUtils.cacheSet(requestData, null);
            this.setState({ status: "dismissed" });
            return;
          }

          if (response?.ok === false) {
            if (response.status === 403) {
              // To be sure that the user has reached the limit, we could also check the response
              // text, but for simplicity we're going to assume that a 403 here means that they've
              // hit the daily limit.
              this.setState({ status: "error", reachedLimit: true });
            }
            this.setState({ status: "error" });
            return;
          }

          if (!response.ok) {
            this.defineErrorHandler(response);
            this.stopFetchTimeoutTimer();
            return;
          }
          this.startFetchTimeoutTimer(requestId);

          const reader = response.body.getReader();
          const processStream = () =>
            reader.read().then(async ({ done, value }) => {
              if (!this.isCurrentRequest(requestId)) {
                // If not, stop processing the stream
                return;
              }

              if (done) {
                this.setState({ status: "generated" });
                smartDictionaryUtils.cacheSet(
                  requestData,
                  this.state.explanation
                );
                this.stopFetchTimeoutTimer();
                this.explanationShown();
                return;
              }
              this.startFetchTimeoutTimer(requestId);

              // Decode the received bytes and update the UI
              const decoded = new TextDecoder("utf-8").decode(value);
              if (decoded !== undefined) {
                await this.setState({
                  explanation: this.state.explanation + decoded,
                });
              }

              // Continue processing the stream
              return processStream();
            });

          return processStream();
        })
        .catch((error) => {
          if (!this.isCurrentRequest(requestId)) {
            return;
          }
          this.defineErrorHandler(error);
          console.error("ERROR: ", error);
          this.setState({
            status: "error",
          });
        });
    } else {
      readlangClient.request(
        "GET",
        "/api/smartDictionary",
        {
          ...requestData,
          cacheOnly: this.props.cacheOnly,
        },
        (explanation) => {
          // An empty response (204) means the user dismissed explanations for
          // this word + context. Always cache; only update state if current.
          smartDictionaryUtils.cacheSet(requestData, explanation || null);
          if (!this.isCurrentRequest(requestId)) {
            return;
          }
          if (!explanation) {
            this.setState({ status: "dismissed" });
            return;
          }
          this.setState({ status: "generated", explanation });
          this.explanationShown();
        },
        (error) => {
          if (!this.isCurrentRequest(requestId)) {
            return;
          }
          if (error.status === 403) {
            // As on the streaming path, assume a 403 means the daily limit was
            // reached: the extension background script drops the non-JSON
            // "definitionLimit" body, so it can't be checked here.
            this.setState({
              status: "error",
              reachedLimit: true,
            });
          } else if (this.props.cacheOnly) {
            // A cache miss (404) — or a terminal failure, which the global
            // notification bar already reported: offer the "Explain" button
            // rather than an error, since the "error" status renders nothing
            // in cacheOnly mode and would leave the pane blank with no retry.
            this.setState({ status: "not-found" });
          } else {
            this.setState({ status: "error" });
          }
        },
        { dataType: "text" }
      );
    }
  }

  defineErrorHandler(error: any) {
    if (error.status === 403) {
      if (typeof error.text === "function") {
        error
          .text()
          .then((text) => {
            if (text === "definitionLimit") {
              this.setState({
                reachedLimit: true,
              });
            }
          })
          .catch((error) => {
            console.error("error.text error: ", error);
          });
        return;
      }
    }
  }

  componentDidUpdate(
    prevProps: Readonly<Props>,
    prevState: Readonly<State>
  ): void {
    if (
      this.props.cacheOnly !== prevProps.cacheOnly ||
      JSON.stringify(createRequestData(this.props)) !==
        JSON.stringify(createRequestData(prevProps))
    ) {
      this.fetchExplanation();
    }

    // Notify the host once when we settle on a state that renders nothing, so a
    // wrapper can close instead of showing an empty pane. Deferred so the host
    // can safely unmount us without re-entering this update.
    const nothingToShow =
      this.state.status === "dismissed" ||
      (this.state.status === "not-found" && !this.props.showExplanationButton);
    if (nothingToShow && prevState.status !== this.state.status) {
      const notify = this.props.onNoExplanation;
      if (notify) {
        setTimeout(notify);
      }
    }
  }

  render() {
    return (
      <div class="explanation">
        {(() => {
          switch (this.state.status) {
            case "loading":
              return this.props.cacheOnly ? null : (
                <p>Generating explanation...</p>
              );

            case "error": {
              if (this.state.reachedLimit) {
                return this.props.cacheOnly ? null : (
                  <p>
                    You've reached your daily limit of{" "}
                    {subscriptionPlans.permissions["free"].definitions}{" "}
                    context-aware explanations. Get unlimited explanations by{" "}
                    <a
                      href="#"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        this.upgrade();
                      }}
                    >
                      upgrading to premium
                    </a>
                    .
                  </p>
                );
              }

              return this.props.cacheOnly ? null : (
                <p>
                  Problem generating explanation.
                  <button
                    class="linkButton explain"
                    onClick={() => {
                      this.fetchExplanation();
                    }}
                  >
                    Try again
                  </button>
                </p>
              );
            }

            case "generating":
              if (this.state.explanation === undefined) {
                return <p>Generating...</p>;
              }

            // Intentional fallthrough to "generated" to show partial result with blinking
            // cursor...
            // eslint-disable-next-line no-fallthrough
            case "generated":
              return (
                <>
                  <p
                    class="explanationText"
                    style={this.explanationDirectionStyle()}
                  >
                    <i class="fa icon-lightbulb"></i> &nbsp;{" "}
                    {this.renderExplanation()}
                    {this.state.status === "generating" ? (
                      <span class="blinkingCursor">▌</span>
                    ) : null}
                  </p>
                  {this.state.status === "generated" && !this.props.cacheOnly
                    ? this.renderReportSection()
                    : null}
                </>
              );

            case "dismissed":
              return null;

            case "not-found": {
              if (this.props.showExplanationButton) {
                return (
                  <button
                    class="linkButton explain"
                    onClick={() => {
                      this.props.onClickGenerate();
                    }}
                  >
                    Explain
                  </button>
                );
              }
              return null;
            }

            default:
              return null;
          }
        })()}
      </div>
    );
  }

  renderExplanation() {
    const explanation = this.state.explanation;

    const parts = convertTextToParts(
      explanation,
      // Don't add buttons for these existing strings
      [this.props.word, ...this.props.existingTranslations],
      this.props.onSelectTranslation ? true : false
    );

    // Consecutive normal/bold sections form a single run, so phrase selection
    // works across formatting boundaries; only suggested-translation buttons
    // break the text up
    const rendered: preact.ComponentChildren[] = [];
    let run: TextSegment[] = [];
    const flushRun = () => {
      if (run.length > 0) {
        rendered.push(this.renderTextRun(run));
        run = [];
      }
    };

    for (const section of parts) {
      if (section.type === "englishTranslation") {
        flushRun();
        rendered.push(
          <button
            class="translationSuggestion"
            onClick={() => {
              this.props.onSelectTranslation?.(section.text);
            }}
          >
            <i class="icon icon-plus"></i> {section.text}
          </button>
        );
      } else {
        run.push({ text: section.text, bold: section.type === "bold" });
      }
    }
    flushRun();

    return rendered;
  }

  // When the explanation isn't in the user's own language (e.g. explanations
  // switched to the learning language), each word is click-to-translatable,
  // matching the behavior of the main reading text
  renderTextRun(segments: TextSegment[]) {
    if (
      this.state.status !== "generated" ||
      this.explanationLanguage() === stores.user.firstLanguage.value
    ) {
      return segments.map((segment) =>
        segment.bold ? (
          <strong class="preserveLineBreaks">{segment.text}</strong>
        ) : (
          <span class="preserveLineBreaks">{segment.text}</span>
        )
      );
    }

    return (
      <span class="preserveLineBreaks">
        <TranslatableTextView
          segments={segments}
          context={this.state.explanation.replace(/\*\*/g, "")}
          sourceLanguage={this.explanationLanguage()}
          targetLanguage={stores.user.firstLanguage.value}
          source={{ source: "explanation" }}
        />
      </span>
    );
  }

  explanationDirectionStyle() {
    return languageMap.map[this.explanationLanguage()].direction === "RTL"
      ? { direction: "rtl" }
      : {};
  }

  renderReportSection() {
    return (
      <p class="reportExplanationTrigger">
        <button
          class="reportExplanationButton"
          onClick={() => this.openReport()}
        >
          <i class="icon-flag"></i> Report bad explanation
        </button>
      </p>
    );
  }

  openReport() {
    explanationReportModal.show({
      word: this.props.word,
      explanation: this.state.explanation,
      requestData: createRequestData(this.props),
      directionStyle: this.explanationDirectionStyle(),
      onAccepted: (newExplanation) =>
        this.acceptRegeneratedExplanation(newExplanation),
      onDismissed: () => this.dismissExplanation(),
    });
  }

  acceptRegeneratedExplanation(newExplanation: string) {
    const requestData = createRequestData(this.props);

    smartDictionaryUtils.cacheSet(requestData, newExplanation);

    if (this.isRequestDataStillValid(requestData)) {
      this.setState({ explanation: newExplanation });
    }
  }

  dismissExplanation() {
    const requestData = createRequestData(this.props);

    // Dismissal applies to the word + context pair, whichever language explanations are shown in
    smartDictionaryUtils.cacheSet(
      { ...requestData, explanationLanguage: this.props.language },
      null
    );
    smartDictionaryUtils.cacheSet(
      { ...requestData, explanationLanguage: stores.user.firstLanguage.value },
      null
    );

    if (this.isRequestDataStillValid(requestData)) {
      this.setState({ status: "dismissed" });
    }
  }

  upgrade() {
    premiumUpsellModal.show({
      title: "explanationsLimit",
      heading:
        "Upgrade for more than " +
        subscriptionPlans.permissions["free"].definitions +
        " explanations per day",
      subheading:
        "Really understand what you're reading with unlimited AI-based context-aware explanations!",
      showTitle: false,
      smartDefinitions: true,
    });
  }
}

type ExplanationPart = {
  // "notSuggestion" is normal text that later patterns mustn't search for
  // suggestions (see `translatesOtherPhrase`)
  type: "normal" | "englishTranslation" | "bold" | "notSuggestion";
  text: string;
};

export const convertTextToParts = (
  input: string,
  exclude: string[],
  detectTranslations: boolean
): ExplanationPart[] => {
  const excludeNormalized = exclude.map((translation) =>
    practiceUtils.normalize(translation)
  );

  // `translatesPrecedingText`: the quote translates whatever precedes `before`
  const patterns = [
    { before: "can mean ", translatesPrecedingText: true },
    { before: "means ", translatesPrecedingText: true },
    { before: "meaning is ", translatesPrecedingText: true },
    { before: " or " },
    { before: ", ", after: ", " },
    { after: " in English" },
    { before: "translated to ", translatesPrecedingText: true },
    { before: "translated as ", translatesPrecedingText: true },
    { before: "translates to ", translatesPrecedingText: true },
    { before: "translated to English as ", translatesPrecedingText: true },
  ];

  let parts: ExplanationPart[] = [{ type: "normal", text: input }];

  if (detectTranslations) {
    for (const pattern of patterns) {
      parts = _.flatten(
        parts.map((part) => {
          switch (part.type) {
            case "normal": {
              return detectEnglishTranslationsFromPattern(
                part.text,
                pattern,
                excludeNormalized
              );
            }

            default:
              return [part];
          }
        })
      );
    }
  }

  parts = _.flatten(
    parts.map((part) => {
      switch (part.type) {
        case "normal":
        case "notSuggestion": {
          return detectBoldText(part.text);
        }

        default:
          return [part];
      }
    })
  );

  return parts;
};

const detectEnglishTranslationsFromPattern = (
  input: string,
  pattern: {
    before?: string;
    after?: string;
    translatesPrecedingText?: boolean;
  },
  excludeNormalized: string[]
): ExplanationPart[] => {
  const parts: ExplanationPart[] = [];
  const lc = "[" + L + "]";
  // Each delimiter form has its own capture group so an apostrophe inside a
  // double-quoted phrase (e.g. "that's fine!") doesn't terminate the match.
  // Punctuation inside the closing quote (American style, e.g. "was beating."
  // or "I meet,") is captured so it can follow the button
  const punctuation = "([\\.,!?]?)";
  const quotedWord =
    "(?:" +
    '["\u201C\u201D](' +
    lc +
    '[^"\u201C\u201D]+' +
    lc +
    ")" +
    punctuation +
    '["\u201C\u201D]' +
    "|'(" +
    lc +
    "[^']+" +
    lc +
    ")" +
    punctuation +
    "'" +
    "|`(" +
    lc +
    "[^`]+" +
    lc +
    ")" +
    punctuation +
    "`" +
    ")";
  const regExp = new RegExp(
    (pattern.before || "") + quotedWord + (pattern.after || ""),
    "gu"
  );
  // Alternative translations following a match, e.g. ` or "I meet"`
  const alternatives = new RegExp("(?:,? or " + quotedWord + ")*", "uy");
  let match = regExp.exec(input);

  let processedUpToIndex = 0;

  /**
   * Used to exclude any strings which are much longer than the longest currently included
   * excluded strings
   */
  const longestExcludeLength = excludeNormalized.reduce(
    (longestLength, current) =>
      current.length > longestLength ? current.length : longestLength,
    0
  );

  while (match) {
    const captured = match[1] || match[3] || match[5];
    if (
      pattern.translatesPrecedingText &&
      translatesOtherPhrase(input.substring(0, match.index), excludeNormalized)
    ) {
      // Don't add buttons for it or its alternatives, e.g. `"lo encuentro cerca"
      // means "I find him nearby" or "I come across him nearby"`
      alternatives.lastIndex = match.index + match[0].length;
      const end = alternatives.lastIndex + alternatives.exec(input)![0].length;
      parts.push(
        {
          type: "normal",
          text: input.substring(processedUpToIndex, match.index),
        },
        { type: "notSuggestion", text: input.substring(match.index, end) }
      );
      processedUpToIndex = end;
      regExp.lastIndex = end;
    } else if (
      !captured ||
      excludeNormalized.includes(practiceUtils.normalize(captured)) ||
      captured.length > 3 * longestExcludeLength
    ) {
      // Don't add button
      parts.push({
        type: "normal",
        text: input.substring(
          processedUpToIndex,
          match.index + match[0].length
        ),
      });
      processedUpToIndex = match.index + match[0].length;
    } else {
      // Add button
      parts.push({
        type: "normal",
        text: input.substring(
          processedUpToIndex,
          match.index + (pattern.before?.length ?? 0)
        ),
      });
      parts.push({
        type: "englishTranslation",
        text: captured,
      });
      const textAfter =
        (match[2] || match[4] || match[6] || "") + (pattern.after || "");
      if (textAfter) {
        parts.push({
          type: "normal",
          text: textAfter,
        });
      }
      processedUpToIndex = match.index + match[0].length;
    }

    // Call again, which will start the search at the point in the input string where the last one
    // left off
    match = regExp.exec(input);
  }

  // Add the last section
  parts.push({
    type: "normal",
    text: input.substring(processedUpToIndex),
  });

  return parts;
};

/**
 * @param textBefore - the explanation up to a translation pattern like "means "
 * @returns whether the translation is of a longer quoted phrase containing the
 *   word or one of its translations, such as the context in `Here, "lo
 *   encuentro cerca" means "I find him nearby"`, so isn't a translation of it
 */
const translatesOtherPhrase = (
  textBefore: string,
  excludeNormalized: string[]
): boolean => {
  // A quoted phrase, optionally followed by one word such as "literally"
  const quotedPhrase = textBefore.match(
    new RegExp(
      "[\"\u201C\u201D'`]([^\"\u201C\u201D'`]+)[\"\u201C\u201D'`] (?:[" +
        L +
        "]+ )?$",
      "u"
    )
  );
  if (!quotedPhrase) {
    return false;
  }
  const phrase = practiceUtils.normalize(quotedPhrase[1]);
  return excludeNormalized.some(
    (excluded) =>
      excluded && phrase !== excluded && ` ${phrase} `.includes(` ${excluded} `)
  );
};

const detectBoldText = (input: string): ExplanationPart[] => {
  const regExp = new RegExp("\\*\\*([^\\*]+)\\*\\*", "g");
  let match = regExp.exec(input);

  const parts = [];

  let processedUpToIndex = 0;

  while (match) {
    parts.push({
      type: "normal",
      text: input.substring(processedUpToIndex, match.index),
    });
    parts.push({
      type: "bold",
      text: match[1],
    });
    processedUpToIndex = match.index + match[0].length;

    // Call again, which will start the search at the point in the input string where the last one
    // left off
    match = regExp.exec(input);
  }

  // Add the last section
  parts.push({
    type: "normal",
    text: input.substring(processedUpToIndex),
  });

  return parts;
};
